import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import lscache from 'lscache'
import countryToCurrency from 'country-to-currency'
import uuid from 'uuid-random'

import api from '@/api'
import config from '../config'
import Jet from '@/modules/Jet'
import Sentry from '@/modules/Sentry'
import { ENV_DEV_OR_STAGING, ENV_HACKERONE } from '@/helpers/environment'
import { SUPPORTED_CURRENCIES, ZERO_DECIMAL_CURRENCIES, GDPR_COUNTRIES } from '@/helpers/constants'

import request, {
	marketingRequest,
	paymentApiAxiosInstance as paymentApi,
	subscriptionApiAxiosInstance as subscriptionApi,
} from '@/helpers/request'
import coreFeatures from '@/store/coreFeatures'
import { bmiCalculator } from '@/helpers/userData'
import { filterInvalidKeys } from '@/helpers/utils'

import i18n from './i18n.js'
import survey from './survey.js'
import location from './location.js'
import status from './status.js'
import userFlags from './userFlags.js'
import onetrust from './onetrust.js'
import paywall from './paywall.js'
import subscription from './subscription.js'
import upsell from './upsell.js'
import products from './products.js'
import palta from './palta.js'
import { WeightLossCalculator } from '@/helpers/weightPredictor'

Vue.use(Vuex)

const ONE_DAY = 24 * 60
const ONE_WEEK = ONE_DAY * 7
const FORCE_EUR_COUNTRIES = ['BG', 'CZ', 'HR', 'HU', 'PL', 'RO']

// Check if preview mode is active (only in dev/staging environments)
const isPreviewMode = () => {
	if (!ENV_DEV_OR_STAGING) {
		return false
	}
	const urlParams = new URLSearchParams(window.location.search)
	return urlParams.has('is_ob_preview')
}

// Create storage based on preview mode
const createStorage = (cacheTime) => {
	if (isPreviewMode()) {
		// Memory-only storage for preview mode
		const storage = {}
		return {
			getItem: (key) => storage[key] || null,
			setItem: (key, value) => {
				storage[key] = value
			},
			removeItem: (key) => {
				delete storage[key]
			},
		}
	}

	// Normal localStorage for production
	return {
		getItem: (key) => lscache.get(key),
		setItem: (key, value) => lscache.set(key, value, cacheTime),
		removeItem: (key) => lscache.remove(key),
	}
}

const storeInstance = new Vuex.Store({
	modules: {
		onetrust,
		survey,
		i18n,
		location,
		status,
		coreFeatures,
		userFlags,
		palta,
		paywall,
		subs: subscription,
		upsell,
		products,
	},

	plugins: [
		createPersistedState({
			key: 'vuex', // Store this data in localStorage for 1 week
			storage: createStorage(ONE_WEEK),
			paths: ['absmartly', 'marketingDeduplicationEventId', 'location', 'survey', 'userFlags', 'email', 'main_user_id'],
		}),
		createPersistedState({
			key: 'onedaystorage', // Store this data in localStorage for 1 day
			storage: createStorage(ONE_DAY),
			paths: ['paywall', 'upsell'],
		}),
		createPersistedState({
			key: 'auth', // Store this data in localStorage forever
			storage: createStorage(), // No cache time = forever
			paths: ['auth', 'location.initialUtmParams', 'auth0'],
		}),
		createPersistedState({
			storage: isPreviewMode() ? createStorage() : window.sessionStorage,
			paths: [
				'geo',
				'name',
				'zip',
				'upsaled',
				'lastEmailSendAt',
				'currency',
				'palta',
				'organicTrafficOptions',
				'loginCredentials',
				'hasPrevSurvey',
				'signedIn',
			],
		}),
	],

	state: {
		auth: {
			token: null,
			refreshToken: null,
			expireAt: null,
		},
		auth0: {
			accessToken: null,
			idToken: null,
			expiresAt: null,
		},
		absmartly: {
			exposures: {},
		},
		notification: null,
		email: '',
		name: '',
		zip: '',
		lastEmailSendAt: null,

		user: null,
		main_user_id: null,

		currency: 'usd',
		upsaled: false,
		hasWorkbookPurchaseError: false,
		geo: null,
		marketingDeduplicationEventId: uuid(),
		appTheme: null,
		organicTrafficOptions: {
			paymentConsent: false,
			paymentConsentError: false,
			upgradeConsent: false,
			upgradeConsentError: false,
		},
		loginCredentials: {
			email: null,
			code: null,
			sentAt: null,
			attempts: 0,
			mode: 'default',
		},
		hasPrevSurvey: false,
		signedIn: false,
		languageModal: {
			showModal: false,
			screenName: '',
		},
	},

	getters: {
		getAuthCredentials(state) {
			return state.auth
		},

		getAuth0Credentials(state) {
			return state.auth0
		},

		isAnonymous(state) {
			return state.user && state.user.isAnonymous
		},

		isAuthenticated(state) {
			return state.user && state.user.isThirdParty
		},

		isOnboarded(state) {
			return state.user && state.user.isOnboarded
		},

		getMeasurementSystem: () => (type) => {
			return ['lbs', 'ft'].includes(type) ? 'imperial' : 'metric'
		},

		getHeightUnitSystem(state, getters) {
			let value = getters['survey/getPropertyValue']('measurement_system_height', true)
			// Set defaults
			if (!value) {
				if (getters.getCountryCode === 'us') {
					value = 'ft'
				} else {
					value = 'cm'
				}
			}
			return value
		},

		getWeightUnitSystem(state, getters) {
			let value = getters['survey/getPropertyValue']('measurement_system_weight', true)
			if (Array.isArray(value)) {
				value = value.toString()
			}

			// Set defaults
			if (!value) {
				const heightUnitSystem = getters['survey/getPropertyValue']('measurement_system_height', true)

				if (heightUnitSystem === 'ft') {
					return 'lbs'
				} else if (heightUnitSystem === 'cm') {
					return 'kg'
				}

				if (getters.getCountryCode === 'us') {
					return 'lbs'
				} else if (getters.getCountryCode === 'gb') {
					return 'st'
				} else {
					return 'kg'
				}
			}

			return value
		},

		getBmi(state, getters) {
			const weight = getters['survey/getPropertyValue']('weight', false)
			const height = getters['survey/getPropertyValue']('height', false)
			if (!weight || !height) {
				return null
			}
			return bmiCalculator(weight, height)
		},

		getUserId(state) {
			return state.main_user_id || state.user?.id
		},

		hasAlreadyExistedAccount(state) {
			return state.main_user_id && state.user?.id && state.main_user_id !== state.user?.id
		},

		getUserEmail(state) {
			return state.email || state.user?.email
		},

		getUserName(state) {
			if (state.user?.name === 'Anonymous') {
				return ''
			}

			return state.user?.name ?? ''
		},

		getRegionCode(state) {
			return state.geo?.region
		},

		getCountryCode(state) {
			return state.geo?.countryCode
		},

		getCountryCodeUppercase(state) {
			return state.geo?.countryCode?.toUpperCase()
		},

		getGeo(state) {
			return state.geo ?? {}
		},

		getGdprStatus(state) {
			return state.geo?.gdpr
		},

		getIsEUCountry(state) {
			return state.geo?.isEUCountry
		},

		getIsRoWCountry(state, getters) {
			return !['US', 'CA', 'GB', 'AU'].includes(getters.getCountryCodeUppercase) && !getters.getIsEUCountry
		},

		getIsLatamCountry(state, getters) {
			return ['MX', 'BR', 'AR', 'CO', 'CL', 'EC', 'UY', 'PE', 'CR', 'PA', 'BO', 'SV', 'PY', 'VE'].includes(
				getters.getCountryCodeUppercase,
			)
		},

		getCurrency(state, getters) {
			return getters['subs/getCurrency'] || state.currency
		},

		getCurrencyUppercase(state, getters) {
			return getters['getCurrency']?.toUpperCase?.()
		},

		getIsZeroDecimalCurrency(state, getters) {
			return ZERO_DECIMAL_CURRENCIES.includes(getters['getCurrency'])
		},

		getAbSmartlyExposures(state) {
			return Object.values(state.absmartly.exposures)
		},
		getAppTheme(state) {
			return state.appTheme ?? 'light'
		},
		getOrganicTrafficOptions(state) {
			return state.organicTrafficOptions
		},
		isSignedIn(state) {
			return state.signedIn
		},
		isPreviewMode(state, getters) {
			if (!ENV_DEV_OR_STAGING) {
				return false
			}
			return Boolean(getters['location/getQueryParam']('is_ob_preview'))
		},
		getLanguageModalState(state) {
			return state.languageModal
		},
	},

	mutations: {
		setUser(state, user) {
			state.user = user
		},

		setAuthCredentials(state, { token, refreshToken, expireAt }) {
			state.auth.token = token
			state.auth.refreshToken = refreshToken
			state.auth.expireAt = expireAt
		},

		setAuth0Credentials(state, { accessToken, idToken, expiresAt }) {
			state.auth0.accessToken = accessToken
			state.auth0.idToken = idToken
			state.auth0.expiresAt = expiresAt
		},

		addAbSmartlyExposure(state, exposure) {
			const { name, iteration, variantLetter, variantName } = exposure
			const key = `${name}_${iteration}`
			const exposureData = { name, iteration, variantLetter, variantName }

			Vue.set(state.absmartly.exposures, key, exposureData)
		},

		removeAbSmartlyExposure(state, { name, iteration }) {
			Vue.delete(state.absmartly.exposures, `${name}_${iteration}`)
		},

		markOnboarded(state) {
			if (state.user) {
				state.user.isOnboarded = true
			}
		},

		resetNotification(state) {
			state.notification = null
		},

		setNotification(state, message) {
			state.notification = message
		},

		saveEmail(state, value) {
			state.email = value
		},

		saveName(state, value) {
			state.name = value
		},

		saveZip(state, value) {
			state.zip = value
		},

		updateLastEmailSendAt(state, value) {
			state.lastEmailSendAt = value
		},

		setCurrency(state, currency) {
			state.currency = currency
		},

		setUpsaled(state, upsaled) {
			state.upsaled = upsaled
		},

		setMainUserId(state, user_id) {
			state.main_user_id = user_id
		},

		setWorkbookPurchaseError(state, value) {
			state.hasWorkbookPurchaseError = value
		},

		setGeo(state, geo) {
			state.geo = geo
		},

		setAppTheme(state, theme) {
			state.appTheme = theme
		},

		setOrganicTrafficOption(state, { key, value } = {}) {
			state.organicTrafficOptions[key] = value
		},

		setLoginCredentials(state, data) {
			state.loginCredentials = data
		},

		setHasPrevSurvey(state, value) {
			state.hasPrevSurvey = value
		},

		setSignedIn(state) {
			state.signedIn = true
		},
		setLanguageModalState(state, { showModal, screenName }) {
			state.languageModal.showModal = showModal
			state.languageModal.screenName = screenName || ''
		},
	},

	actions: {
		async setUserData(store, user) {
			store.commit('setUser', user)

			let userId = user.id

			const isCrmAutologin = store.getters['location/isCrmAutologin']
			const isInAppAutologin = store.getters['location/isInAppAutologin']
			const isCallAutologin = store.getters['location/isCallAutologin']

			if (isCrmAutologin || isInAppAutologin || isCallAutologin) {
				const userIdFromQueryParam = store.getters['location/getQueryParam']('user')
				const userEmailFromQueryParam = store.getters['location/getQueryParam']('email')
				const userNameFromQueryParam = store.getters['location/getQueryParam']('userName')
				const userHeightFromQueryParam = store.getters['location/getQueryParam']('userHeight')
				const userWeightFromQueryParam = store.getters['location/getQueryParam']('userWeight')
				const userTargetWeightFromQueryParam = store.getters['location/getQueryParam']('userTargetWeight')
				const userAgeFromQueryParam = store.getters['location/getQueryParam']('userAge')
				const userSexFromQueryParam = store.getters['location/getQueryParam']('userSex')

				if (uuid.test(userIdFromQueryParam)) {
					userId = userIdFromQueryParam
					store.commit('setMainUserId', userIdFromQueryParam)
				}

				if (userEmailFromQueryParam) {
					store.commit('saveEmail', userEmailFromQueryParam)
				}

				if (userNameFromQueryParam) {
					store.commit('saveName', userNameFromQueryParam)
				}

				if (userHeightFromQueryParam) {
					store.commit('survey/updateProperty', { property: 'height', value: parseInt(userHeightFromQueryParam) })
				}

				if (userWeightFromQueryParam) {
					store.commit('survey/updateProperty', { property: 'weight', value: parseInt(userWeightFromQueryParam) })
				}

				if (userTargetWeightFromQueryParam) {
					store.commit('survey/updateProperty', {
						property: 'targetWeight',
						value: parseInt(userTargetWeightFromQueryParam),
					})
				}

				if (userAgeFromQueryParam) {
					store.commit('survey/updateProperty', {
						property: 'age',
						value: parseInt(userAgeFromQueryParam),
						isData: true,
					})
				}

				if (userSexFromQueryParam) {
					store.commit('survey/updateProperty', { property: 'sex', value: userSexFromQueryParam })
				}
			}

			this._vm.$analytic.setUserId(userId)
			Jet.setUser({ userId: userId })
			window.dataLayer?.push({ user_id: userId })
			Sentry.setUser()
		},

		setupPreviewUser(store) {
			const mockUser = {
				id: 'preview-user-12345',
				email: 'test@test.test',
				name: 'Preview User',
				isThirdParty: true,
				isOnboarded: true,
				isAnonymous: false,
				weight: 70,
				height: 170,
				targetWeight: 65,
				age: 30,
				sex: 'female',
				data: JSON.stringify({
					main_goal: ['lose_weight'],
					current_priorities: ['healthy_eating'],
					focus_areas: ['core'],
					measurement_system_height: 'cm',
					measurement_system_weight: 'kg',
					workout_level: ['beginner'],
					work: ['office_work'],
				}),
			}

			// Set user data
			store.commit('setUser', mockUser)
			store.commit('saveEmail', mockUser.email)
			store.commit('saveName', mockUser.name)

			// Set survey data to avoid onboarding redirects
			store.commit('survey/updateProperty', { property: 'sex', value: mockUser.sex })
			store.commit('survey/updateProperty', { property: 'weight', value: mockUser.weight })
			store.commit('survey/updateProperty', { property: 'height', value: mockUser.height })
			store.commit('survey/updateProperty', { property: 'targetWeight', value: mockUser.targetWeight })
			store.commit('survey/updateProperty', { property: 'age', value: mockUser.age, isData: true })

			// Set flags to appear fully onboarded
			store.commit('userFlags/updateLocalFlags', {
				freebieAccess: false,
			})

			// Set signed in state
			store.commit('setSignedIn')
		},
		async fetchUserFromAws(store, { includeExpired = false } = {}) {
			// Skip API call in preview mode
			if (store.getters.isPreviewMode) {
				store.dispatch('setupPreviewUser')
				return
			}
			try {
				if (store.getters['status/resourceIsPending']('user')) {
					return
				}

				store.commit('status/setResourcePending', 'user')

				const response = await api.getUser()

				await store.dispatch('setUserData', response.data)
				await store.dispatch('loadSubscription', { includeExpired })

				if (response.data.data) store.dispatch('mergeUserData')
			} catch (e) {
				this._vm.$analytic.logEvent('Get user error', { e })
			} finally {
				store.commit('status/unsetResourcePending', 'user')
			}
		},

		async loadSubscription(store, { retry = false, retryNumber = 0, includeExpired = false } = {}) {
			let isError = false

			if (retry) {
				retryNumber += 1
			}

			try {
				const userId = store.getters['getUserId']
				await store.dispatch('subs/fetchSubscription', {
					customer_id: userId,
					includeExpired: includeExpired,
				})

				if (!store.getters['subs/hasSubscription']) {
					if (retry && retryNumber < 3) {
						await new Promise((resolve) => setTimeout(resolve, 500 * retryNumber))
						return store.dispatch('loadSubscription', { retry: true, retryNumber })
					}
				}
			} catch (e) {
				isError = true

				if (retryNumber < 3) {
					await new Promise((resolve) => setTimeout(resolve, 500 * retryNumber))
					return store.dispatch('loadSubscription', { retry: true, retryNumber })
				}
			} finally {
				const subscription = store.getters['subs/getSubscription']

				this._vm.$analytic.logEvent('WebGetActiveSubscription', {
					error: isError,
					retry_number: retryNumber,
					price_point_id: subscription?.product_id ?? 'none',
					subscription_id: subscription?.subscription_id ?? 'none',
				})
			}
		},

		switchPaltaSubscription(store, params) {
			return store.dispatch('palta/switchSubscription', params)
		},

		makePaltaPurchase(store, params) {
			return store.dispatch('palta/purchase', params).then((result) => {
				return result.data
			})
		},

		async checkExistingUserByEmail(store, params) {
			const response = await api.userCheck(params)
			const userId = response.data

			store.commit('setMainUserId', userId)
			Sentry.setUser()

			await store.dispatch('loadSubscription')

			return userId
		},

		async sendConfirmEmail(store, params) {
			await api.sendConfirmEmail({
				email: store.state.email,
				...params,
			})
			store.commit('updateLastEmailSendAt', new Date())
		},

		async createPaymentCustomer(store) {
			let email = store.getters['getUserEmail']
			let name = store.getters['getUserName']
			let user_id = store.getters['getUserId']
			let abSmartlyExposures = store.getters['getAbSmartlyExposures']

			await fetch(`${config('SimpleAPIUrl')}v1/api/customers`, {
				method: 'POST',
				body: JSON.stringify({
					user_id,
					email,
					name,
					experiments: abSmartlyExposures,
				}),
			}).catch((error) => {
				console.error(error.response)
			})
		},

		async fetchPaymentCustomer({ getters, commit }) {
			return await subscriptionApi
				.get(`billing/v1/customers/${getters.getUserId}`)
				.then((response) => {
					const paymentMethod = response?.data?.payment_method
					if (paymentMethod) {
						commit('paywall/setLastUsedProvider', paymentMethod.provider)
					}
					return response?.data
				})
				.catch(() => null)
		},

		/**
		 * Method for sending marketing campaign data to Payment API
		 * @param store
		 * @param {object} data
		 * @param {'facebook'|'google'|'impact'|'twitter'} data.source - source of campaign
		 * @param {object} data.params
		 */
		sendMarketingCampaignData(store, data) {
			const { source, params } = data

			return fetch(`${config('SimpleAPIUrl')}v1/api/campaign-tracker/${source}`, {
				method: 'POST',
				body: JSON.stringify(params),
			})
				.then(() => {
					if (ENV_DEV_OR_STAGING) {
						/* eslint-disable no-console */
						console.groupCollapsed(
							`%cMarketing campaign tracker [debug]: %c${source}`,
							'font-weight: 400',
							'color: #19ffb2',
						)
						console.log(data)
						console.groupEnd()
						/* eslint-enable no-console */
					}
				})
				.catch((error) => {
					Sentry.withScope((scope) => {
						scope.setExtras({ source, ...params })
						scope.setTags({
							marketingCampaignTrackerError: true,
						})
						scope.captureException(error)
					})
				})
		},

		async createMeasurement(store) {
			const userWeight = store.state.user.weight

			if (typeof userWeight !== 'number' || userWeight <= 0) {
				Sentry.withScope((scope) => {
					scope.setExtras({ user: store.state.user, userWeight })
					scope.setTags({
						emailSavingError: true,
						emailSavingErrorType: 'incorrect_measurements',
					})
					scope.captureMessage('Incorrect weight at user/measurements')
				})
			}

			return await api.createMeasurement({
				records: [
					{
						type: 'Weight',
						value: userWeight,
						source: 'Manual',
						date: new Date(),
						secondsFromGMT: -new Date().getTimezoneOffset() * 60,
					},
				],
			})
		},

		/**
		 * Get country code and currency from https://assets.simple.org/geo.json
		 * @returns {Promise<{countryCode: String, currency: String}>}
		 */
		async fetchSimpleGeo(store, { countryOverride, regionOverride }) {
			return request(config('GeoAPIUrl')).then((response = {}) => {
				let { city, continent, country, isEUCountry, region, regionCode, postalCode } = response

				if (countryOverride) {
					country = countryOverride
				}

				if (regionOverride) {
					regionCode = regionOverride
				}

				let currency = countryToCurrency[country] ?? 'USD'
				if (FORCE_EUR_COUNTRIES.includes(country)) {
					currency = 'EUR'
				}

				const result = {
					countryCode: country?.toLowerCase() ?? 'us',
					currency: currency,
					continentCode: continent,
					city: city?.toLowerCase(),
					isEUCountry: isEUCountry === '1',
					regionName: region?.toLowerCase(),
					region: regionCode?.toLowerCase(),
					zip: postalCode,
				}

				return filterInvalidKeys(result)
			})
		},

		async fetchIpWhoIsGeo() {
			return request('https://ipwho.is/')
		},

		/**
		 * Fetch geo data and set it to the store
		 * @param store
		 * @returns {Promise<void>}
		 */
		async fetchGeo(store, { country, region }) {
			let currencyCode = 'usd'

			if (store.state.geo && !country) {
				return
			}

			if (ENV_DEV_OR_STAGING || ENV_HACKERONE) {
				const queryParamCountry = country || store.getters['location/getQueryParam']('country')
				if (queryParamCountry && /^[A-Z]{2}$/.test(queryParamCountry)) {
					country = queryParamCountry
					region = region || store.getters['location/getQueryParam']('region')

					store.commit('setCurrency', currencyCode)

					let currency = countryToCurrency[country] ?? 'USD'
					if (FORCE_EUR_COUNTRIES.includes(country)) {
						currency = 'EUR'
					}

					store.commit('setGeo', {
						countryCode: country.toLowerCase(),
						region: region,
						currency: currency,
						gdpr: GDPR_COUNTRIES.includes(country),
					})
				}
			}

			const simpleGeoResult = await store.dispatch('fetchSimpleGeo', {
				countryOverride: country,
				regionOverride: region,
			})

			if (!simpleGeoResult) {
				return
			}

			if (SUPPORTED_CURRENCIES.includes(simpleGeoResult.currency)) {
				currencyCode = simpleGeoResult.currency?.toLowerCase()
			}

			store.commit('setCurrency', currencyCode)
			store.commit('setGeo', {
				...simpleGeoResult,
				gdpr: GDPR_COUNTRIES.includes(simpleGeoResult.countryCode?.toUpperCase()),
			})
		},

		async fetchUserLocalTax(store) {
			const hasRequiredGeoFields = store.state.geo && ['countryCode', 'currency'].every((key) => key in store.state.geo)

			if (!hasRequiredGeoFields) {
				return Promise.resolve(null)
			}

			const params = {
				user_id: store.getters['getUserId'],
				geo_data: store.state.geo,
			}

			return await fetch(`${config('SimpleAPIUrl')}v1/api/geodata`, {
				method: 'POST',
				body: JSON.stringify(params),
			})
				.then((response) => response.json())
				.catch((error) => {
					Sentry.withScope((scope) => {
						scope.setExtras({ params, queryParams: store.state.location.queryParams })
						scope.setTags({
							paymentApiError: true,
						})
						scope.captureException(error)
					})
				})
		},

		logout() {
			localStorage.clear()
			window.location.href = '/'
		},

		mergeUserData({ state }) {
			let user = state.user
			let data = user.data ? JSON.parse(user.data) : {}

			Object.keys(state.user)
				.filter((key) => key in state.survey.values)
				.forEach((key) => {
					if (key == 'data') {
						Object.keys(data)
							.filter((data_key) => data_key in state.survey.values.data)
							.forEach((data_key) => {
								state.survey.values.data[data_key] = data[data_key] === 'null' ? null : data[data_key]
							})
					} else {
						const surveyValues = state.survey.values[key]
						const userValues = state.user[key]

						if (Array.isArray(surveyValues) && Array.isArray(userValues)) {
							const uniqueValues = Array.from(new Set([...surveyValues, ...userValues]))
							state.survey.values[key] = uniqueValues
						} else {
							state.survey.values[key] = userValues
						}
					}
				})
			// Fix for users who came from mobile onboarding which does not
			// set measurement_system_height and measurement_system_weight
			// and instead using measurement_system for both height and weight
			// doesn't change already set value
			const msHeight = state.survey.values.data.measurement_system_height
			const msWeight = state.survey.values.data.measurement_system_weight
			if (state.survey.values.data.measurement_system && (!msHeight || !msWeight)) {
				const isMetric = state.survey.values.data.measurement_system?.toString() === 'metric'
				state.survey.values.data.measurement_system_height = msHeight || (isMetric ? 'cm' : 'ft')
				state.survey.values.data.measurement_system_weight = msWeight || (isMetric ? 'kg' : 'lbs')
			}
		},

		async activateFreebieAccess({ getters }, freebieCode) {
			return await paymentApi.post(`v1/api/free-access/activate`, {
				code: freebieCode,
				user_id: getters.getUserId,
			})
		},

		async sendReferralCode({ getters }, referralCode) {
			return await subscriptionApi.post(`/billing/v1/customer/${getters.getUserId}/partners`, {
				referral_code: referralCode,
			})
		},

		sendAbSmartlyExposuresToBraze(store, exposures) {
			if (!exposures) {
				exposures = store.state.absmartly.exposures
			}

			for (let exposureName in exposures) {
				const { name, iteration, variantLetter, variantName } = exposures[exposureName]
				const underscoredName = name.replaceAll(' ', '_')

				this._vm.$appboy.logCustomEvent('Experiment - Exposure', {
					Name: name,
					Iteration: `i${iteration}`,
					Variant: variantLetter,
					VariantName: variantName,
				})
				this._vm.$appboy.addToCustomAttributeArray(
					'active_test_groups_web',
					`${underscoredName}(${variantLetter})(${`i${iteration}`})`,
				)
			}
		},

		async updateCustomerInfo({ getters }, options) {
			return await paymentApi.patch(`v1/customers/${getters.getUserId}`, { ...options })
		},

		async saveUserMarketingParams(context, params) {
			try {
				await marketingRequest('/actions/user_click', {
					method: 'POST',
					data: params,
				})
			} catch (error) {
				// Just let the user pass anyway
			}
		},

		async sendMarketingPurchaseTrackEvent({ getters }) {
			await marketingRequest('actions/purchase_track/frontend', {
				method: 'put',
				params: {
					user_id: getters.getUserId,
				},
			})
		},

		sendRenewalReminder(store, params) {
			return subscriptionApi.post(`/billing/v1/subscription/reminder`, {
				subscription_id: params.subscriptionId,
				has_reminder: params.hasReminder,
			})
		},

		getRenewalReminder(store, params) {
			return subscriptionApi.get(`/billing/v1/subscription/${params.subscriptionId}/reminder`)
		},
	},
})

storeInstance.watch(
	(rootState, getters) => ({
		weightInKg: getters['survey/userWeightInKg'],
		heightInCm: getters['survey/userHeightInCm'],
		targetWeightInKg: getters['survey/userTargetWeightInKg'],
	}),
	(newVal, oldVal) => {
		if (!newVal) {
			return
		}
		const { weightInKg, heightInCm, targetWeightInKg } = newVal
		if (
			oldVal &&
			oldVal.weightInKg === weightInKg &&
			oldVal.heightInCm === heightInCm &&
			oldVal.targetWeightInKg === targetWeightInKg
		) {
			return
		}
		if (weightInKg && heightInCm && targetWeightInKg) {
			const predicts = WeightLossCalculator.predictWeightLoss({
				weight: weightInKg,
				weightGoal: targetWeightInKg,
				height: heightInCm,
			})

			storeInstance.commit('survey/setWeightPredicts', predicts)
			const dateEnd = predicts[predicts.length - 1].targetDate
			storeInstance.commit('survey/updateProperty', { isData: false, value: dateEnd, property: 'dateEnd' })
			storeInstance.commit('survey/updateProperty', { isData: true, value: dateEnd, property: 'dateEnd' })
		}
	},
	{ immediate: true },
)

export default storeInstance
